import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  CountrySelect,
  InfoBar,
  Link,
  StateSelect,
  TextField,
  type TextFieldProps,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type { InputHTMLAttributes } from "react";
import { useWatch } from "react-hook-form";

import { checkoutBillingErrorContactUsURL } from "../../../../config";
import { formDataToBillingDetails } from "../../../../hooks/useCheckout";
import { useTracker } from "../../../../hooks/useTracker";
import useTranslation from "../../../../hooks/useTranslation";
import { ElementClickAction } from "../../../../lib/TrackingAdapter";
import { ServiceErrorCode } from "../../../../types";
import { isCountryInEU } from "../../../../utils/countryUtils";
import {
  type CheckoutFormData,
  COUNTRIES_WITH_MANDATORY_STATE_SELECTOR,
  MANDATORY_BILLING_DATA_COUNTRIES,
  MANDATORY_BILLING_DATA_FIELDS,
} from "../../ChargebeeCheckoutSchema";
import { useCheckoutFormContext } from "../CheckoutFormProvider";

import style from "./BillingInfoForm.module.css";

const BillingDetailsStateSelect = ({
  name,
  ...args
}: InputHTMLAttributes<HTMLSelectElement> & TextFieldProps & { name: keyof CheckoutFormData }) => {
  const {
    methods: {
      register,
      formState: { errors },
      control,
      setValue,
    },
  } = useCheckoutFormContext();

  const countryCode = useWatch({ name: "countryCode", control }) ?? "";
  useEffect(() => {
    setValue(name, "");
  }, [countryCode, name, setValue]);

  if (!COUNTRIES_WITH_MANDATORY_STATE_SELECTOR.includes(countryCode)) {
    return null;
  }

  return (
    <StateSelect
      {...args}
      //eslint-disable-next-line
      countryCode={countryCode as any}
      defaultValue=""
      required
      errorMessage={errors[name]?.message}
      {...register(name)}
    />
  );
};

const BillingDetailsTextField = ({
  name,
  ...args
}: InputHTMLAttributes<HTMLInputElement> & TextFieldProps & { name: keyof CheckoutFormData }) => {
  const {
    methods: {
      register,
      formState: { errors },
    },
  } = useCheckoutFormContext();

  return (
    <TextField
      {...args}
      {...register(name)}
      autoComplete="off"
      autoSave="off"
      autoCapitalize="off"
      autoCorrect="off"
      aria-autocomplete="none"
      errorMessage={errors[name]?.message}
    />
  );
};

export const useAllMandatoryFieldsFilled = () => {
  const {
    methods: { control },
  } = useCheckoutFormContext();
  const formData = useWatch({ control }) as CheckoutFormData;

  const countryCode = useWatch({ control, name: "countryCode" });
  const isBillingRequired = MANDATORY_BILLING_DATA_COUNTRIES.includes(countryCode || "");

  const mandatoryBillingFieldNames = useMemo((): (keyof CheckoutFormData)[] => {
    if (!isBillingRequired) return [];
    const names = [...MANDATORY_BILLING_DATA_FIELDS] as (keyof CheckoutFormData)[];
    if (countryCode && COUNTRIES_WITH_MANDATORY_STATE_SELECTOR.includes(countryCode)) {
      names.push("stateCode");
    }
    return names;
  }, [countryCode, isBillingRequired]);

  return useMemo(
    () =>
      mandatoryBillingFieldNames.every((field) => {
        const value = formData[field];
        return typeof value === "string" && value.trim() !== "";
      }),
    [formData, mandatoryBillingFieldNames]
  );
};

export default function BillingInfoForm() {
  const { t, m } = useTranslation();
  const {
    methods: {
      register,
      setValue,
      control,
      formState: { errors, isLoading },
    },
    estimateMutationResult,
  } = useCheckoutFormContext();

  const tracker = useTracker();

  const { data: estimateData, isPending: isEstimatePending } = estimateMutationResult ?? {};
  const hasBillingErrorFromEstimate = estimateData?.errors?.some(
    (error) => error.code === ServiceErrorCode.ValidationError
  );
  const countryCode = useWatch({ control, name: "countryCode" });
  const isBillingRequired = MANDATORY_BILLING_DATA_COUNTRIES.includes(countryCode || "");
  const allMandatoryFilled = useAllMandatoryFieldsFilled();

  const shouldShowBillingError =
    hasBillingErrorFromEstimate &&
    !(isBillingRequired && !allMandatoryFilled) &&
    !isEstimatePending;

  const formData = useWatch({ control }) as CheckoutFormData;
  const billingDetails = useMemo(() => formDataToBillingDetails(formData), [formData]);
  const hasBillingInfo = Object.entries(billingDetails ?? {}).some(
    ([key, value]) => key != "countryCode" && key != "migrationConsent" && value
  );
  const [isBillingOpen, setIsBillingOpen] = useState(false);
  useEffect(() => {
    if (!isLoading && (isBillingRequired || hasBillingInfo)) {
      setIsBillingOpen(true);
    }
  }, [isBillingRequired, hasBillingInfo, setValue, isLoading]);

  const onClickAddCompanyDetails = useCallback(() => {
    setIsBillingOpen(true);
    tracker.trackButtonClick({
      elementId: "add_billing_details",
      action: ElementClickAction.ADD_BILLING_DETAILS,
      details: "chargebee",
    });
  }, [tracker]);

  return (
    <>
      <div className={style.subgroup}>
        <div className={style.checkoutLineText}>
          {t(m.Checkout.BillingInfo.Title)}
          <span>{isBillingRequired ? "" : t(m.Checkout.BillingInfo.Optional)}</span>
        </div>
        <div>{t(m.Checkout.BillingInfo.Description)}</div>
      </div>
      <div className={style.subgroup}>
        {!isBillingOpen && (
          <Button variant="grayscale" onClick={onClickAddCompanyDetails}>
            {t(m.Checkout.BillingInfo.AddCompanyDetails)}
          </Button>
        )}
        {isBillingOpen && shouldShowBillingError && (
          <InfoBar
            variant="critical"
            title={t(m.Checkout.BillingInfo.VerificationErrorTitle)}
            text={
              <>
                <span>{t(m.Checkout.BillingInfo.VerificationErrorText)}</span>
                <br />
                <Link href={checkoutBillingErrorContactUsURL} target="_blank">
                  {t(m.Checkout.BillingInfo.ContactUs)}
                </Link>
              </>
            }
          />
        )}
        <div className={classNames(style.companyDetailsForm, !isBillingOpen && style.hide)}>
          <BillingDetailsTextField
            name="company"
            placeholder={t(m.Checkout.BillingInfo.NameOnBill)}
            maxLength={250}
          />
          <BillingDetailsTextField
            name="line1"
            placeholder={t(m.Checkout.BillingInfo.AddressLine1)}
            maxLength={150}
          />
          <BillingDetailsTextField
            name="line2"
            placeholder={
              !isBillingRequired
                ? t(m.Checkout.BillingInfo.AddressLine2)
                : t(m.Checkout.BillingInfo.AddressLine2Optional)
            }
            maxLength={150}
          />
          <BillingDetailsTextField
            name="city"
            placeholder={t(m.Checkout.BillingInfo.City)}
            maxLength={50}
          />
          <div className={style.creditCardLocationContainer}>
            <CountrySelect
              required
              errorMessage={errors.countryCode?.message}
              aria-label="Country"
              {...register("countryCode")}
            />
            <BillingDetailsTextField
              name="zip"
              placeholder={t(m.Checkout.BillingInfo.PostCode)}
              maxLength={20}
            />
          </div>
          <BillingDetailsStateSelect name="stateCode" />
          <BillingDetailsTextField
            name="vatNumber"
            maxLength={20}
            placeholder={
              isCountryInEU(countryCode)
                ? t(m.Checkout.BillingInfo.VatNumber)
                : t(m.Checkout.BillingInfo.TaxId)
            }
          />
        </div>
      </div>
    </>
  );
}
